import { createFileRoute, Link } from "@tanstack/react-router"
import { AlertTriangle, FileCheck, Scale, ScrollText } from "lucide-react"

import { generateMetaTags } from "@repo/seo/generate-meta-tags"
import { sharedConfig } from "~/lib/config/shared"

export const Route = createFileRoute("/_public-layout/terms-of-service")({
  component: TermsOfServiceScreen,
  beforeLoad: async () => {
    return {
      title: "Terms of Service",
    }
  },
  head: ({ match }) => ({
    meta: generateMetaTags({
      base: {
        title: `${match.context.title} | ${sharedConfig.appName}`,
        description: `Terms of Service for ${sharedConfig.appName}`,
        url: sharedConfig.baseUrl + match.fullPath,
      },
    }),
  }),
})

function TermsOfServiceScreen() {
  return (
    <div className="container mx-auto px-4 py-16 md:px-6">
      <div className="mx-auto max-w-4xl">
        <div className="mb-10">
          <Link
            to="/"
            className="mb-6 flex items-center gap-2 text-primary hover:underline"
          >
            ← Back to Home
          </Link>
          <h1 className="mb-4 font-bold text-4xl tracking-tight">
            Terms of Service
          </h1>
          <p className="text-muted-foreground text-xl">
            Please read these terms carefully before using our services.
          </p>
        </div>

        <div className="mb-10 rounded-lg bg-muted p-8">
          <div className="mb-4 flex items-center gap-3">
            <Scale className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">Agreement to Terms</h2>
          </div>
          <p className="mb-4 text-muted-foreground">
            These Terms of Service constitute a legally binding agreement
            between you and {sharedConfig.appName} regarding your use of our
            website and services.
          </p>
          <p className="text-muted-foreground">
            By accessing or using our website or services, you agree to be bound
            by these Terms. If you do not agree to these Terms, you may not
            access or use our website or services.
          </p>
        </div>

        <section className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <FileCheck className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">Use of Services</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="mb-2 font-medium text-lg">Account Registration</h3>
              <p className="text-muted-foreground">
                Some features of our services may require you to register for an
                account. You agree to provide accurate, current, and complete
                information during the registration process and to update such
                information to keep it accurate, current, and complete.
              </p>
              <p className="mt-2 text-muted-foreground">
                You are responsible for safeguarding your account credentials
                and for all activities that occur under your account. You agree
                to notify us immediately of any unauthorized use of your
                account.
              </p>
            </div>

            <div>
              <h3 className="mb-2 font-medium text-lg">Acceptable Use</h3>
              <p className="text-muted-foreground">
                You agree not to use our services for any purpose that is
                unlawful or prohibited by these Terms. You may not use our
                services in any manner that could damage, disable, overburden,
                or impair our servers or networks, or interfere with any other
                party's use of our services.
              </p>
              <p className="mt-2 text-muted-foreground">
                You may not attempt to gain unauthorized access to any part of
                our services, other accounts, computer systems, or networks
                connected to our servers through hacking, password mining, or
                any other means.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <ScrollText className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">Intellectual Property</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="mb-2 font-medium text-lg">Our Content</h3>
              <p className="text-muted-foreground">
                Unless otherwise indicated, our website and services, including
                all content, features, and functionality, are owned by{" "}
                {sharedConfig.appName} or its licensors and are protected by
                copyright, trademark, patent, trade secret, and other
                intellectual property laws.
              </p>
              <p className="mt-2 text-muted-foreground">
                You may not reproduce, distribute, modify, create derivative
                works of, publicly display, publicly perform, republish,
                download, store, or transmit any of our content without our
                express prior written permission.
              </p>
            </div>

            <div>
              <h3 className="mb-2 font-medium text-lg">Your Content</h3>
              <p className="text-muted-foreground">
                You may be able to upload, submit, store, send or receive
                content through our services. You retain ownership of any
                intellectual property rights that you hold in that content.
              </p>
              <p className="mt-2 text-muted-foreground">
                By uploading, submitting, storing, sending or receiving content
                through our services, you give us and our affiliates a worldwide
                license to use, host, store, reproduce, modify, create
                derivative works, communicate, publish, publicly perform,
                publicly display and distribute such content.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-10">
          <div className="mb-4 flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-primary" />
            <h2 className="font-semibold text-2xl">Disclaimer of Warranties</h2>
          </div>

          <p className="mb-4 text-muted-foreground">
            OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
            WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT
            NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
            PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
          </p>

          <p className="text-muted-foreground">
            WE DO NOT WARRANT THAT OUR SERVICES ARE ACCURATE, RELIABLE, CURRENT,
            OR ERROR-FREE, OR THAT THEY WILL MEET YOUR REQUIREMENTS OR BE
            AVAILABLE ON AN UNINTERRUPTED, SECURE, OR ERROR-FREE BASIS.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 font-semibold text-2xl">
            Limitation of Liability
          </h2>
          <p className="text-muted-foreground">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL{" "}
            {sharedConfig.appName.toUpperCase()}, ITS DIRECTORS, EMPLOYEES,
            PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
            INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL,
            OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF
            INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF OR
            INABILITY TO ACCESS OR USE THE SERVICES.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 font-semibold text-2xl">Indemnification</h2>
          <p className="text-muted-foreground">
            You agree to defend, indemnify, and hold harmless{" "}
            {sharedConfig.appName}, its officers, directors, employees, and
            agents, from and against any claims, liabilities, damages,
            judgments, awards, losses, costs, expenses, or fees (including
            reasonable attorneys' fees) arising out of or relating to your
            violation of these Terms or your use of the Services.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 font-semibold text-2xl">Governing Law</h2>
          <p className="text-muted-foreground">
            These Terms shall be governed by and construed in accordance with
            the laws of the State of California, without regard to its conflict
            of law provisions. Any legal action or proceeding arising out of or
            relating to these Terms or your use of our services shall be brought
            exclusively in the federal or state courts located in San Francisco
            County, California, and you consent to the personal jurisdiction of
            such courts.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="mb-4 font-semibold text-2xl">Changes to Terms</h2>
          <p className="text-muted-foreground">
            We reserve the right to modify these Terms at any time. If we make
            changes to these Terms, we will provide notice of such changes by
            updating the date at the bottom of these Terms and by maintaining a
            current version of the Terms on our website. Your continued use of
            our services following the posting of revised Terms means that you
            accept and agree to the changes.
          </p>
          <p className="mt-4 text-muted-foreground text-sm">
            Last Updated: June 2023
          </p>
        </section>

        <section className="rounded-lg bg-muted p-8">
          <h2 className="mb-4 font-semibold text-2xl">Contact Us</h2>
          <p className="mb-4 text-muted-foreground">
            If you have any questions about these Terms, please contact us:
          </p>

          <div className="space-y-2 text-muted-foreground">
            <p>Email: legal@{sharedConfig.appName.toLowerCase()}.com</p>
            <p>
              Address: 123 Innovation Drive, Suite 200, San Francisco, CA 94103
            </p>
          </div>
        </section>
      </div>
    </div>
  )
}
